import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Plus as PlusIcon } from "@phosphor-icons/react/Plus";
import { Banner } from "../../components/banner";
import { EmptyState } from "../../components/empty-state";
import { FilterButton } from "../../components/filter-button";
import { DebouncedSearchBar } from "../partner-templates/components";
import { PLAN_NAME } from "../partner-templates/components/subscription-catalog";
import type {
	Account,
	BillingCycle,
	PartnerWriteError,
	PlanTier,
	PortfolioFilters,
	PortfolioSortKey,
	PortfolioStatusTab,
} from "../partner-templates/types";
import { PartnerCancellationTemplate } from "../partner-cancellation-template";
import {
	CycleFilterPopover,
	PlanFilterPopover,
} from "./portfolio-filter-popovers";
import { PortfolioTable } from "./portfolio-table";

const STATUS_TABS: { value: PortfolioStatusTab; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "TRIAL", label: "Trial" },
	{ value: "PAID", label: "Active" },
	{ value: "PAST_DUE", label: "Past due" },
	{ value: "CLOSED", label: "Closed" },
];

const SORT_OPTIONS: { value: PortfolioSortKey; label: string }[] = [
	{ value: "newest", label: "Newest" },
	{ value: "oldest", label: "Oldest" },
	{ value: "nameAsc", label: "Name A→Z" },
	{ value: "nameDesc", label: "Name Z→A" },
	{ value: "renewalSoonest", label: "Renewal soonest" },
	{ value: "renewalLatest", label: "Renewal latest" },
	{ value: "amountHigh", label: "Recurring high→low" },
	{ value: "amountLow", label: "Recurring low→high" },
];

export interface PortfolioStatusCounts {
	ALL: number;
	TRIAL: number;
	PAID: number;
	PAST_DUE: number;
	CLOSED: number;
}

export interface PartnerPortfolioListTemplateProps {
	accounts: Account[];
	statusCounts?: PortfolioStatusCounts;
	totalCount?: number;
	loadingFirstPage?: boolean;
	errorMessage?: string | null;
	suspended?: boolean;
	managingAccountId?: string | null;
	managingErrorMessage?: string | null;
	filters: PortfolioFilters;
	sort: PortfolioSortKey;
	page: number;
	rowsPerPage: number;
	onFiltersChange: (next: PortfolioFilters) => void;
	onSortChange: (next: PortfolioSortKey) => void;
	onPaginationChange: (event: { page: number; rowsPerPage: number }) => void;
	onClearFilters?: () => void;
	onOpenAccount?: (id: string) => void;
	onManageAccount?: (id: string) => void;
	onCreateAccount?: () => void;
	onOpenSubscription?: (id: string) => void;
	/** Fires when the user clicks "End trial" in a row's action menu. Parent should set `confirmingAction` to show the dialog. */
	onRequestEndTrial?: (accountId: string) => void;
	/** Fires when the user clicks "Cancel subscription" in a row's action menu. */
	onRequestCancelSubscription?: (accountId: string) => void;
	/** Fires when the user clicks "Delete account" in a row's action menu. */
	onRequestDeleteAccount?: (accountId: string) => void;
	/** Fires when the user clicks "Start a trial" in a CLOSED account's action menu. */
	onRestartTrial?: (accountId: string) => void;
	/** Currently-confirming action; when non-null the corresponding dialog is open. */
	confirmingAction?: {
		type: "end-trial" | "cancel" | "delete";
		account: Account;
	} | null;
	/** Loading state for the currently-confirming action. */
	confirmationLoading?: boolean;
	/** Error from the currently-confirming action's mutation. */
	confirmationError?: PartnerWriteError | null;
	/** Fires when the user clicks the confirm button in the dialog. Parent reads `confirmingAction.type` to dispatch the right mutation. */
	onConfirmAction?: () => void;
	/** Fires when the user closes/cancels the dialog. */
	onCloseConfirmation?: () => void;
	onRetryLoad?: () => void;
}

export function PartnerPortfolioListTemplate({
	accounts,
	statusCounts,
	totalCount,
	loadingFirstPage = false,
	errorMessage = null,
	suspended = false,
	managingAccountId = null,
	managingErrorMessage = null,
	filters,
	sort,
	page,
	rowsPerPage,
	onFiltersChange,
	onSortChange,
	onPaginationChange,
	onClearFilters,
	onOpenAccount,
	onManageAccount,
	onCreateAccount,
	onOpenSubscription,
	onRequestEndTrial,
	onRequestCancelSubscription,
	onRequestDeleteAccount,
	onRestartTrial,
	confirmingAction = null,
	confirmationLoading = false,
	confirmationError = null,
	onConfirmAction,
	onCloseConfirmation,
	onRetryLoad,
}: PartnerPortfolioListTemplateProps): React.JSX.Element {
	const hasFiltersApplied =
		filters.search !== "" ||
		filters.plan.length > 0 ||
		filters.cycle.length > 0 ||
		filters.status !== "";

	const counts = statusCounts ?? {
		ALL: totalCount ?? accounts.length,
		TRIAL: 0,
		PAID: 0,
		PAST_DUE: 0,
		CLOSED: 0,
	};

	const tabCount = (value: PortfolioStatusTab): number =>
		value === "" ? counts.ALL : counts[value];

	const handleStatusChange = (
		_: React.SyntheticEvent,
		value: PortfolioStatusTab,
	) => {
		onFiltersChange({ ...filters, status: value });
		onPaginationChange({ page: 0, rowsPerPage });
	};

	const handleSortChange = (event: SelectChangeEvent) => {
		onSortChange(event.target.value as PortfolioSortKey);
	};

	return (
		<Stack spacing={3}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={3}
				sx={{ alignItems: "flex-start" }}>
				<Box sx={{ flex: "1 1 auto" }}>
					<Typography variant="h4">Client accounts</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 0.5 }}>
						{totalCount !== undefined && totalCount !== accounts.length
							? `${accounts.length} of ${totalCount} accounts`
							: `${accounts.length} accounts`}
					</Typography>
				</Box>
				<Tooltip
					title={suspended ? "Your partner account is suspended" : ""}
					disableHoverListener={!suspended}>
					<span>
						<Button
							variant="contained"
							startIcon={<PlusIcon />}
							onClick={onCreateAccount}
							disabled={suspended}>
							Create client account
						</Button>
					</span>
				</Tooltip>
			</Stack>

			{managingErrorMessage && (
				<Banner
					severity="error"
					description={managingErrorMessage}
				/>
			)}

			<Card>
				<Tabs
					value={filters.status}
					onChange={handleStatusChange}
					sx={{ px: 3 }}
					variant="scrollable">
					{STATUS_TABS.map((tab) => (
						<Tab
							key={tab.value}
							value={tab.value}
							label={tab.label}
							iconPosition="end"
							icon={
								<Chip
									label={tabCount(tab.value)}
									size="small"
									variant="outlined"
								/>
							}
							sx={{ minHeight: "auto" }}
							tabIndex={0}
						/>
					))}
				</Tabs>
				<Divider />

				<Stack
					direction="row"
					spacing={2}
					sx={{
						alignItems: "center",
						flexWrap: "wrap",
						p: 2,
					}}>
					<Stack
						direction="row"
						spacing={2}
						sx={{
							alignItems: "center",
							flex: "1 1 auto",
							flexWrap: "wrap",
						}}>
						<Box sx={{ minWidth: 240, flex: "0 1 320px" }}>
							<DebouncedSearchBar
								value={filters.search}
								onChange={(value) =>
									onFiltersChange({ ...filters, search: value })
								}
							/>
						</Box>
						<FilterButton
							label="Plan"
							value={filters.plan.length > 0 ? filters.plan : undefined}
							displayValue={
								filters.plan.length > 0
									? filters.plan.map((p) => PLAN_NAME[p] ?? p).join(", ")
									: undefined
							}
							onFilterApply={(value) =>
								onFiltersChange({
									...filters,
									plan: (value as PlanTier[] | undefined) ?? [],
								})
							}
							onFilterDelete={() => onFiltersChange({ ...filters, plan: [] })}
							popover={<PlanFilterPopover />}
						/>
						<FilterButton
							label="Billing cycle"
							value={filters.cycle.length > 0 ? filters.cycle : undefined}
							displayValue={
								filters.cycle.length > 0
									? filters.cycle
											.map((c) => (c === "ANNUAL" ? "Annual" : "Monthly"))
											.join(", ")
									: undefined
							}
							onFilterApply={(value) =>
								onFiltersChange({
									...filters,
									cycle: (value as BillingCycle[] | undefined) ?? [],
								})
							}
							onFilterDelete={() => onFiltersChange({ ...filters, cycle: [] })}
							popover={<CycleFilterPopover />}
						/>
						{hasFiltersApplied && (
							<Button onClick={onClearFilters}>Clear filters</Button>
						)}
					</Stack>
					<Select
						value={sort}
						onChange={handleSortChange}
						size="small"
						sx={{ minWidth: 200 }}>
						{SORT_OPTIONS.map((opt) => (
							<MenuItem
								key={opt.value}
								value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</Stack>
				<Divider />

				{errorMessage ? (
					<Box sx={{ p: 4 }}>
						<Banner
							severity="error"
							title="Failed to load accounts"
							description={errorMessage}
							action={
								onRetryLoad
									? { label: "Retry", onClick: onRetryLoad }
									: undefined
							}
						/>
					</Box>
				) : loadingFirstPage ? (
					<Paper
						elevation={0}
						sx={{ p: 6, display: "flex", justifyContent: "center" }}>
						<CircularProgress />
					</Paper>
				) : accounts.length === 0 ? (
					<Box sx={{ p: 4 }}>
						{hasFiltersApplied ? (
							<EmptyState
								title="No matches"
								description={
									filters.search
										? `No accounts matched "${filters.search}".`
										: "No accounts matched the active filters."
								}
								primaryActionLabel="Clear filters"
								onPrimaryAction={onClearFilters}
							/>
						) : (
							<EmptyState
								title="No client accounts yet"
								description="Create your first managed client account to get started."
								primaryActionLabel="Create client account"
								onPrimaryAction={onCreateAccount}
							/>
						)}
					</Box>
				) : (
					<PortfolioTable
						accounts={accounts}
						managingAccountId={managingAccountId}
						suspended={suspended}
						onOpenAccount={onOpenAccount}
						onManageAccount={onManageAccount}
						onOpenSubscription={onOpenSubscription}
						onRequestEndTrial={onRequestEndTrial}
						onRequestCancelSubscription={onRequestCancelSubscription}
						onRequestDeleteAccount={onRequestDeleteAccount}
						onRestartTrial={onRestartTrial}
					/>
				)}

				<Divider />
				<TablePagination
					component="div"
					count={totalCount ?? accounts.length}
					page={page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
					onPageChange={(_, nextPage) =>
						onPaginationChange({ page: nextPage, rowsPerPage })
					}
					onRowsPerPageChange={(event) =>
						onPaginationChange({
							page: 0,
							rowsPerPage: Number.parseInt(event.target.value, 10),
						})
					}
					labelRowsPerPage="Clients per page:"
					labelDisplayedRows={({ from, to, count }) =>
						`${from}–${to} of ${
							count === -1 ? `more than ${to}` : count
						} clients`
					}
				/>
			</Card>
			{confirmingAction && (
				<PartnerCancellationTemplate
					open
					variant={
						confirmingAction.type === "end-trial"
							? "end-trial-early"
							: confirmingAction.type === "cancel"
								? "cancel-paid"
								: "delete-account"
					}
					requireTypedConfirmation={confirmingAction.type === "delete"}
					account={confirmingAction.account}
					loading={confirmationLoading}
					error={confirmationError}
					onEndTrialEarly={onConfirmAction}
					onCancelSubscription={onConfirmAction}
					onDelete={onConfirmAction}
					onClose={onCloseConfirmation ?? (() => undefined)}
				/>
			)}
		</Stack>
	);
}
